import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListHeader from "@/common/components/ListHeader";
import TextField from "@/common/components/TextField";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { FileBadgeIcon, SearchIcon } from "lucide-react";
import { useGeneralDocuments } from "../../hooks/useGeneralDocuments";
import useDebounce from "@/common/hooks/useDebounce";
import { GeneralDocumentData } from "../../types/generalDocuments.type";
import GeneralDocumentGridItem from "../../components/GeneralDocumentGridItem";
import { cn } from "@/common/utils/cn";
import { Pagination } from "@/common/components/Table/pagination";
import Skeleton from "@/common/components/Skeleton";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";

const GeneralDocumentsList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search);

  const {
    data: generalDocuments,
    meta: generalDocumentMeta,
    isPending: generalDocumentsIsPending,
    error: generalDocumentsError = {} as any,
    refetch: refetchGeneralDocuments,
  } = useGeneralDocuments<GeneralDocumentData[]>({
    filters: {
      search,
      page: Number(filterParams.page) || 1,
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex">
        <ListHeader
          Icon={(
            <FileBadgeIcon
              className="w-10 h-10"
              strokeWidth={1.5}
            />
          )}
          title="Documentos"
        />
      </div>

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={generalDocumentsError?.status}
        refetchFunction={refetchGeneralDocuments}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-lg"
              value={filterParams.search}
              onChange={(e: any) => {
                setFilterParams((params) => ({ ...params, search: e.target.value }));
                setSearch(e.target.value);
              }}
            />
          </div>
        </div>

        {generalDocumentsIsPending && (
          <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(350px,2fr))] gap-4 overflow-y-auto max-h-[calc(100vh-232px)]">
            {new Array(10).fill(null).map((_, i) => (
              <Skeleton key={`loading_document_${i}`} height={136} className="bg-white-100 rounded-lg " />
            ))}
          </div>
        )}

        {!generalDocumentsIsPending && generalDocuments.length === 0 && (
          <EmptyDataMessage className="h-[calc(100vh-232px)]" />
        )}

        {!generalDocumentsIsPending && generalDocuments.length > 0 && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fill,minmax(350px,2fr))] gap-4 overflow-y-auto h-[calc(100vh-232px)]">
              {generalDocuments.map((generalDocument) => (
                <GeneralDocumentGridItem key={generalDocument.id} generalDocument={generalDocument} />
              ))}
            </div>

            <div
              className={cn(
                'w-full px-4 py-2 bg-black-300 rounded-lg flex justify-between items-center sticky bottom-0',
                typeof generalDocumentMeta.total === 'undefined' && 'justify-end',
              )}
            >
              {typeof generalDocumentMeta.total !== 'undefined' && (
                <p className="text-xs italic font-light">
                  Mostrando {generalDocuments.length} resultados de {generalDocumentMeta.total}
                </p>
              )}

              <Pagination
                currentPage={Number(filterParams.page) || 1}
                setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
                totalPages={generalDocumentMeta.total_pages}
              />
            </div>
          </div>
        )}
      </ErrorMessageWrapper>
    </div>
  );
}

export default GeneralDocumentsList;
